/**
 * Previews of the trails (TRAILS) for the profile editor. The game engine draws the real
 * in-game trails; these only need to show what each choice looks like.
 */
import type { TrailId } from '@cube-rush/shared';
import type { Ctx2D } from '../../render/icons';

export interface TrailPoint {
  x: number;
  y: number;
  /** Stable per point, drives the random-looking jitter of particles. */
  id: number;
}

export interface TrailColors {
  primary: string;
  secondary: string;
}

const RAINBOW = ['#ff3b5c', '#ff9a1f', '#ffd60a', '#22e07a', '#2de2ff', '#8c3dff'];

/**
 * Draws a trail through `points` (oldest first) behind a cube of edge `size`.
 * `time` (seconds) animates particles that float on their own.
 */
export function drawTrail(
  ctx: Ctx2D,
  trail: TrailId,
  points: readonly TrailPoint[],
  colors: TrailColors,
  size: number,
  time: number,
): void {
  if (points.length < 2) return;
  ctx.save();
  switch (trail) {
    case 'none':
      break;
    case 'classic':
      drawRibbon(ctx, points, [colors.primary], size * 0.55);
      break;
    case 'rainbow':
      drawRibbon(ctx, points, RAINBOW, size * 0.8);
      break;
    case 'sparks':
      drawSparks(ctx, points, colors, size, time);
      break;
    case 'bubbles':
      drawBubbles(ctx, points, colors, size, time);
      break;
    case 'fire':
      drawFire(ctx, points, size, time);
      break;
  }
  ctx.restore();
}

/** Age of point `i` in [0, 1]: 0 = newest, 1 = oldest. */
function age(i: number, count: number): number {
  return 1 - i / (count - 1);
}

/** Deterministic noise in [0, 1) for a point id and a channel. */
function noise(id: number, channel: number): number {
  let h = Math.imul(id ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(channel + 1, 0xc2b2ae35);
  h ^= h >>> 15;
  h = Math.imul(h, 0x2c1b3c6d);
  h ^= h >>> 12;
  return (h >>> 0) / 4294967296;
}

/**
 * Tapering band made of parallel stripes (one stripe = the classic streak). Each stripe is
 * one filled polygon that follows the path's normals, so the band bends smoothly, and it
 * fades out along its length.
 */
function drawRibbon(
  ctx: Ctx2D,
  points: readonly TrailPoint[],
  stripes: readonly string[],
  width: number,
): void {
  const count = points.length;
  const normals = points.map((_, i) => {
    const before = points[Math.max(0, i - 1)]!;
    const after = points[Math.min(count - 1, i + 1)]!;
    const dx = after.x - before.x;
    const dy = after.y - before.y;
    const length = Math.hypot(dx, dy) || 1;
    return { x: -dy / length, y: dx / length };
  });
  const oldest = points[0]!;
  const newest = points[count - 1]!;
  const stripe = width / stripes.length;
  for (let s = 0; s < stripes.length; s++) {
    const near = (s - stripes.length / 2) * stripe;
    const far = near + stripe;
    ctx.beginPath();
    for (let i = 0; i < count; i++) {
      const p = points[i]!;
      const n = normals[i]!;
      const life = 1 - age(i, count);
      ctx.lineTo(p.x + n.x * near * life, p.y + n.y * near * life);
    }
    for (let i = count - 1; i >= 0; i--) {
      const p = points[i]!;
      const n = normals[i]!;
      const life = 1 - age(i, count);
      ctx.lineTo(p.x + n.x * far * life, p.y + n.y * far * life);
    }
    ctx.closePath();
    const color = stripes[s]!;
    const fade = ctx.createLinearGradient(oldest.x, oldest.y, newest.x, newest.y);
    fade.addColorStop(0, withAlpha(color, 0));
    fade.addColorStop(1, withAlpha(color, 0.9));
    ctx.fillStyle = fade;
    ctx.fill();
  }
}

/** `#rrggbb` → `rgba(r, g, b, alpha)`. */
function withAlpha(hex: string, alpha: number): string {
  const value = Number.parseInt(hex.slice(1), 16);
  return `rgba(${(value >> 16) & 255}, ${(value >> 8) & 255}, ${value & 255}, ${alpha})`;
}

function drawSparks(
  ctx: Ctx2D,
  points: readonly TrailPoint[],
  colors: TrailColors,
  size: number,
  time: number,
): void {
  for (let i = 0; i < points.length; i += 2) {
    const p = points[i]!;
    const old = age(i, points.length);
    const r = size * (0.08 + noise(p.id, 0) * 0.14) * (1 - old * 0.6);
    const x = p.x + (noise(p.id, 1) - 0.5) * size * 0.8;
    const y = p.y + (noise(p.id, 2) - 0.5) * size * 0.9 + Math.sin(time * 6 + p.id) * size * 0.05;
    ctx.globalAlpha = (1 - old) * 0.95;
    ctx.fillStyle = noise(p.id, 3) < 0.5 ? colors.secondary : '#ffffff';
    ctx.beginPath();
    ctx.moveTo(x, y - r * 2);
    ctx.lineTo(x + r * 0.6, y);
    ctx.lineTo(x, y + r * 2);
    ctx.lineTo(x - r * 0.6, y);
    ctx.closePath();
    ctx.fill();
  }
}

function drawBubbles(
  ctx: Ctx2D,
  points: readonly TrailPoint[],
  colors: TrailColors,
  size: number,
  time: number,
): void {
  ctx.lineWidth = Math.max(1, size * 0.06);
  for (let i = 0; i < points.length; i += 3) {
    const p = points[i]!;
    const old = age(i, points.length);
    const r = size * (0.12 + noise(p.id, 0) * 0.16) * (0.6 + old * 0.8);
    const x = p.x + (noise(p.id, 1) - 0.5) * size * 0.5 + Math.sin(time * 3 + p.id) * size * 0.06;
    const y = p.y - old * size * 0.9 + (noise(p.id, 2) - 0.5) * size * 0.6;
    ctx.globalAlpha = (1 - old) * 0.9;
    ctx.strokeStyle = colors.secondary;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.stroke();
    ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
    ctx.beginPath();
    ctx.arc(x - r * 0.35, y - r * 0.35, r * 0.22, 0, Math.PI * 2);
    ctx.fill();
  }
}

function drawFire(ctx: Ctx2D, points: readonly TrailPoint[], size: number, time: number): void {
  for (let i = 0; i < points.length; i++) {
    const p = points[i]!;
    const old = age(i, points.length);
    const r = size * (0.34 - old * 0.28) * (0.7 + noise(p.id, 0) * 0.6);
    if (r <= 0) continue;
    const x = p.x + (noise(p.id, 1) - 0.5) * size * 0.35;
    const y = p.y - old * size * 0.7 + Math.sin(time * 9 + p.id) * size * 0.04;
    ctx.globalAlpha = (1 - old) * 0.85;
    ctx.fillStyle = old < 0.25 ? '#fff27a' : old < 0.55 ? '#ffa51f' : '#ff3b2f';
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
}
